import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { LogType } from '../models/log.model';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { setIsLoadingCountState } from './appSlice';
import { ISystemConfigurationsSyncStatus } from '../models/dms/systemConfiguration.model';
import { getDatabaseTimeOffsetMinutes } from '../api/portal/database.api';

interface ISystemConfigurationsSyncState {
  systemConfigurationsSync?: ISystemConfigurationsSyncStatus;
  systemConfigurationsSyncLoading: boolean;
}

const initialState: ISystemConfigurationsSyncState = {
  systemConfigurationsSync: {
    hasSync: false,
    timestamp: 0,
    isProcessing: false,
    data: {
      databaseTimeOffset: 0,
    },
  },
  systemConfigurationsSyncLoading: false,
};

/// THUNKS

export const setSystemConfigurationsSyncStoreAsync = createAsyncThunk<{}, ISystemConfigurationsSyncStatus | undefined>('systemConfigurationsSync/setSystemConfigurationsSyncStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting system configurations to storage`, '[systemConfigurationsSync] setSystemConfigurationsSyncStoreAsync');
  try {
    dispatch(setSystemConfigurationsSyncState(val));
    await updateConfigObject<ISystemConfigurationsSyncStatus>(ConfigName.SystemConfigurationsSync, val || ({} as ISystemConfigurationsSyncStatus), false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setSystemConfigurationsProcessingStoreAsync = createAsyncThunk<{}, boolean>('systemConfigurationsSync/setSystemConfigurationsProcessingStoreAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, '', '[systemConfigurationsSync] setSystemConfigurationsProcessingStoreAsync');
  try {
    dispatch(setSystemConfigurationsProcessingState(val));
    await updateConfigObject<ISystemConfigurationsSyncStatus>(
      ConfigName.SystemConfigurationsSync,
      {
        ...(getState() as RootState).systemConfigurationsSync.systemConfigurationsSync,
        isProcessing: val,
      } as ISystemConfigurationsSyncStatus,
      false,
    );
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setSystemConfigurationsHasSyncStoreAsync = createAsyncThunk<{}, boolean>('systemConfigurationsSync/setSystemConfigurationsHasSyncStoreAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, '', '[systemConfigurationsSync] setSystemConfigurationsHasSyncStoreAsync');
  try {
    dispatch(setSystemConfigurationsHasSyncState(val));
    await updateConfigObject<ISystemConfigurationsSyncStatus>(
      ConfigName.SystemConfigurationsSync,
      {
        ...(getState() as RootState).systemConfigurationsSync.systemConfigurationsSync,
        hasSync: val,
        isProcessing: false,
      } as ISystemConfigurationsSyncStatus,
      false,
    );
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchSystemConfigurationsStoreAsync = createAsyncThunk<{}, void>('systemConfigurationsSync/fetchSystemConfigurationsStoreAsync', async (nil, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Fetching T&Cs from database`, '[systemConfigurationsSync] fetchSystemConfigurationsStoreAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setSystemConfigurationsSyncLoadingState(true));

    const state = getState() as RootState;
    const { systemConfigurationsSync } = state.systemConfigurationsSync;

    let systemConfigurationsSyncState = { ...systemConfigurationsSync } as ISystemConfigurationsSyncStatus;

    try {
      const databaseTimeOffset = await getDatabaseTimeOffsetMinutes();

      systemConfigurationsSyncState = {
        ...systemConfigurationsSyncState,
        timestamp: new Date().getTime(),
        hasSync: true,
        isProcessing: false,
        data: {
          databaseTimeOffset,
        },
      };

      await dispatch(setSystemConfigurationsSyncStoreAsync(systemConfigurationsSyncState));
      await updateConfigObject<ISystemConfigurationsSyncStatus>(ConfigName.SystemConfigurationsSync, systemConfigurationsSyncState, false);

      return systemConfigurationsSyncState.data;
    } catch (ex: any) {
      logTransaction(LogType.Error, `Failed to execute fetchSystemConfigurationsStoreAsync thunk. ${ex?.message}`);
    } finally {
      dispatch(setIsLoadingCountState(-1));
      dispatch(setSystemConfigurationsSyncLoadingState(false));
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setSystemConfigurationsSyncLoadingState(false));
  }
});

/// SLICE

export const systemConfigurationsSyncSlice = createSlice({
  name: 'systemConfigurationsSync',
  initialState,
  reducers: {
    setSystemConfigurationsSyncState: (state, action: PayloadAction<ISystemConfigurationsSyncStatus | undefined>) => {
      logConsole(LogType.Info, '[systemConfigurationsSync] setSystemConfigurationsSyncState');
      state.systemConfigurationsSync = action.payload;
    },
    setSystemConfigurationsProcessingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[systemConfigurationsSync] setSystemConfigurationsProcessingState: ${action.payload}`);
      if (state.systemConfigurationsSync) {
        state.systemConfigurationsSync.isProcessing = action.payload;
      }
    },
    setSystemConfigurationsHasSyncState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[systemConfigurationsSync] setSystemConfigurationsHasSyncState: ${action.payload}`);
      if (state.systemConfigurationsSync) {
        state.systemConfigurationsSync.hasSync = action.payload;
        state.systemConfigurationsSync.isProcessing = false;
      }
    },
    setSystemConfigurationsSyncLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[systemConfigurationsSync] setSystemConfigurationsSyncLoadingState: ${action.payload}`);
      state.systemConfigurationsSyncLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setSystemConfigurationsSyncState, setSystemConfigurationsProcessingState, setSystemConfigurationsHasSyncState, setSystemConfigurationsSyncLoadingState } = systemConfigurationsSyncSlice.actions;

export default systemConfigurationsSyncSlice.reducer;
